import { useState, useRef } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { SkillRatingStars } from './SkillRatingStars'
import type { LanguageSkill } from '../types'
import type { SkillRating } from '../../../types/skills'
import { LANGUAGE_TOOLTIPS } from '../../../types/skills'

interface LanguageSkillsSectionProps {
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  skills: LanguageSkill[]
  onChange: (skills: LanguageSkill[]) => void
  onDelete: (skillId: string) => void
  prSheetId?: string
}

export function LanguageSkillsSection({
  expanded,
  onExpandedChange,
  skills,
  onChange,
  onDelete,
  prSheetId,
}: LanguageSkillsSectionProps) {
  const [newSkillName, setNewSkillName] = useState('')
  const [isComposing, setIsComposing] = useState(false) // IME入力中フラグ
  const timeoutRef = useRef<number | null>(null)

  const handleAddSkill = (valueToAdd?: string) => {
    const trimmedName = (valueToAdd ?? newSkillName).trim()
    if (!trimmedName) return

    // 既に同じ名前のスキルが存在する場合は追加しない（重複防止）
    if (skills.some((s) => s.Name === trimmedName)) {
      setNewSkillName('')
      return
    }

    const newSkill: LanguageSkill = {
      Id: `temp-lang-${Date.now()}`,
      Name: trimmedName,
      Rating__c: undefined,
      Description__c: '',
      PRSheet__c: prSheetId,
      SkillGroup__c: undefined,
      SortOrder__c: skills.length,
    }

    onChange([...skills, newSkill])
    // 即座にクリア
    setNewSkillName('')
  }

  const handleSkillChange = (index: number, field: keyof LanguageSkill, value: string | number | undefined) => {
    const updated = [...skills]
    updated[index] = { ...updated[index], [field]: value }
    onChange(updated)
  }

  const handleDeleteSkill = (skillId: string, index: number) => {
    if (skillId.startsWith('temp-')) {
      // 一時IDの場合はstateから削除するだけ
      const updated = skills.filter((_, i) => i !== index)
      onChange(updated)
    } else {
      // 実際のIDの場合は削除リストに追加
      onDelete(skillId)
      const updated = skills.filter((_, i) => i !== index)
      onChange(updated)
    }
  }

  return (
    <Accordion expanded={expanded} onChange={(_, isExpanded) => onExpandedChange(isExpanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" fontWeight="bold">
          言語スキル
          {skills.length > 0 && (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: '#667eea' }}>
              ({skills.length})
            </Typography>
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* 既存スキル */}
          {skills.map((skill, index) => (
            <Box
              key={skill.Id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                padding: 1.5,
                backgroundColor: '#f9f9f9',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <TextField
                  value={skill.Name || ''}
                  onChange={(e) => handleSkillChange(index, 'Name', e.target.value)}
                  placeholder="言語名"
                  size="small"
                  sx={{ flex: 1, mr: 2 }}
                />
                <IconButton onClick={() => handleDeleteSkill(skill.Id!, index)} color="error" size="small">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="textSecondary">
                  レーティング
                </Typography>
                <SkillRatingStars
                  rating={(skill.Rating__c as SkillRating) || null}
                  onChange={(rating) => handleSkillChange(index, 'Rating__c', rating ?? undefined)}
                  tooltips={LANGUAGE_TOOLTIPS}
                />
              </Box>
              <TextField
                value={skill.Description__c || ''}
                onChange={(e) => handleSkillChange(index, 'Description__c', e.target.value)}
                placeholder="説明（任意）"
                size="small"
                multiline
                rows={2}
              />
            </Box>
          ))}

          {/* 新規追加フォーム */}
          <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
            <TextField
              value={newSkillName}
              onChange={(e) => setNewSkillName(e.target.value)}
              placeholder="新しい言語スキルを追加"
              size="small"
              fullWidth
              onCompositionStart={() => setIsComposing(true)} // IME入力開始
              onCompositionEnd={(e) => {
                // IME確定後に次のイベントループで状態をリセット
                setIsComposing(false)
                // IME確定時に入力された値を即座に反映
                const target = e.target as HTMLInputElement
                setNewSkillName(target.value)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (!isComposing) {
                    // IME入力中でない場合のみ追加処理を実行
                    e.preventDefault()
                    // 既存のタイムアウトをクリア（競合防止）
                    if (timeoutRef.current) clearTimeout(timeoutRef.current)
                    // 次のイベントループで実行（IME確定後の値を確実に取得）
                    timeoutRef.current = window.setTimeout(() => {
                      const target = e.target as HTMLInputElement
                      handleAddSkill(target.value)
                      timeoutRef.current = null
                    }, 0)
                  }
                }
              }}
            />
            <Button
              variant="contained"
              onClick={() => handleAddSkill()}
              startIcon={<AddIcon />}
              disabled={!newSkillName.trim()}
              sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
            >
              追加
            </Button>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}
